import React, { useEffect, useState } from "react";
import { Box, Typography, Grid2, Paper, Select, InputLabel, FormControl, MenuItem, Stack, IconButton, Button, useTheme, useMediaQuery, Card, CardContent, CardHeader, Avatar, Container, LinearProgress } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Guage from "../Components/Guage/Guage";
import { DateTime } from "luxon";
import useGetSemesters from "../Hooks/useGetSemester";
import { useDispatch, useSelector } from "react-redux";
import useGetLocations from "../Hooks/useGetLocation";
import API from "../api/api";
import { ArrowCircleDown, FilterListOutlined } from "@mui/icons-material";
import { ClockIcon, DatePicker } from "@mui/x-date-pickers";
import { setSnackError } from '../Reducers/snackErrors';
import { parseSentimentData } from "./AdminHomeHelper";
import Gate from "./Gate";
import { ROOT } from "../Static/static";
import ExecutivePulse from "./AdminDashboard/ExecutivePulse";
import DashboardNav from "./AdminDashboard/DashboardNav";
import OperationalTrends from "./AdminDashboard/OperationalTrends";
import ImpactAnalytics from "./AdminDashboard/ImpactAnalytics";
import LogAnalytics from "./AdminDashboard/LogAnalytics";
import AdminFilterBar from "./AdminDashboard/AdminFilterbar";


const AdminHome = () => {

    const dispatch = useDispatch();
    const role = useSelector((state) => state.role);
    const sysRole = useSelector((state) => state.role.role);

    const perms = useSelector((state) => state.role.permissions);
    const canLocal  = perms?.includes("view:analytics:local") ?? false;
    
    const [guageData, setGuageData] = useState({
        sessionCount: 0,
        studentCount: 0,
        averageSessionDuration: 0,
        assessmentCount: 0,
        passRate: 0,
        averageStudentPerSession: 0

    });

    const [loading, setLoading] = useState(false);


    const [sessionsBChart, setSessionBChart] = useState([]);
    const [assessmentBChart, setAssessmentBChart] = useState([]);
    const [programsBChart, setProgramsBChart] = useState([]);
    const [tutorsBChart, setTutorsBChart] = useState([]);
    const [assessmentTrendData, setAssessmentTrend] = useState([]);
    const [sessionTrendData, setSessionTrend] = useState([]);
    const [lowPerformanceTutors, setLowPerformanceTutors] = useState([]);
    const [cycleGrowth, setCycleGrowth] = useState([]);
    const [cycleGrowthDelim, setCycleDelimGrowth] = useState([]);
    const [sentimentData, setSentimentData] = useState([]);
    const [absentPresent, setAbsentPresent] = useState({});
    const [assessmentCompletion, setAssessmentCompletion] = useState({});
    const [sessionVScore, setSessionVScore] = useState([]);
    const [studentVScore, setStudentVScore] = useState([]);
    const [tutorsSessions, setTutorsSessions] = useState([]);
    const [locationAverageSessions, setLocationSessions] = useState([]);
    


    const { semesters, loading: semestersLoading, error: semestersError } = useGetSemesters(role);
    const { locations, loading: locationsLoading, error: locationsError } = useGetLocations(role);

    const [dates, setDate] = useState({
        Today: DateTime.local().toSQLDate(),
        ThisWeek: `${DateTime.local().startOf('week').toSQLDate() }`+ "-" + `${DateTime.local().endOf('week').toSQLDate()}`,
        LastWeek: `${DateTime.local().startOf('week').minus({days:7}).toSQLDate() }`+"-"+`${DateTime.local().endOf('week').minus({days:7}).toSQLDate()}`,
        LastMonth: `${DateTime.local().startOf('month').toSQLDate() }`+"-"+`${DateTime.local().endOf('month').toSQLDate()}`,
    })



    const fallback = (req) => {
      return (
        <Paper sx={{ p: 2 }}>
          <Container>
          <Typography variant="body2" color="text.secondary">
              You do not have access { req }
            </Typography>
          </Container>
          </Paper>
      )
    }

    const initialValues = {
        semester_id: null, // Initially null
        location_id: null, // Initially null
        start_date: null,
        end_date: null
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        setLoading(true);
        let start_date_parse, end_date_parse = null;
        if (values.start_date) {
          start_date_parse = DateTime.fromJSDate(new Date(values.start_date)).toSQLDate()
        }
        if (values.end_date) {
          end_date_parse = DateTime.fromJSDate(new Date(values.end_date)).toSQLDate()  
        }
        const promises = [
          loadGuageData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadSessionBChartData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadAssessmentBChart(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadProgramsBChart(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadTutorsBChart(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadLowPerformanceTutorsData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadCycleGrowthData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadCycleGrowthDataPrePostMid(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadAbsentPresentData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadAssessmentCompletion(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadSentimentData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadSessionVScores(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadStudentVAssessments(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadTutorRecentSessions(values.location_id, values.semester_id),
          loadLocationSessionAverage(values.location_id, values.semester_id),
        ];
        // Await all promises to complete in parallel
        await Promise.all(promises).then((success) => {
          console.log(success)
          setLoading(false);
        })
        .catch((error => {
          console.log(error);
          setLoading(false);
        }))
        
    };


    

    const loadGuageData = async (location_id, semester_id, start_date, end_date) => {
        if (!location_id || !semester_id) { return ;}
        try {
            const request = await API.get('/get_session_analytics', {params:{ location_id, semester_id, start_date, end_date} });
            if (request.data){
              const sessionData = request.data.session_analytics;
              const assessmentData = request.data.assessment_session_analytics;
              setGuageData({
                sessionCount: sessionData.session_count,
                studentCount: sessionData.student_count,
                averageSessionDuration: sessionData.average_session_duration,
                passRate: assessmentData.pass_rate,
                assessmentCount: assessmentData.assessment_counts,
                averageStudentPerSession: sessionData.average_student_session,
                
              })
            }
        }
        catch(error) {
          console.log(error)
          if (error.isAuthError || error.isForbidden) {
              return;
            } 
            if (error.isForbidden) {
              return;
            }
            if (error.isNetworkError) {
              dispatch(setSnackError({ 
                title: 'Connection Error', 
                body: error.message 
              }));
            } else {
              dispatch(setSnackError({
                title: 'Error found',
                body: error.message || 'Unable to get data.'
              }));
            }
          return;
        }
    }

    const loadSessionBChartData = async (location_id, semester_id, start_date, end_date) => {
        try {
            const request = await API.get('/get_session_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
              location_id, semester_id, start_date, end_date }});
              if (request.status === 200) {
                setSessionBChart(request.data.data);
              }
        }
        catch(error) {
          console.log(error)
          if (error.isAuthError || error.isForbidden) {
              return;
            } 
            if (error.isForbidden) {
              return;
            }
            if (error.isNetworkError) {
              dispatch(setSnackError({ 
                title: 'Connection Error', 
                body: error.message 
              }));
            } else {
              dispatch(setSnackError({
                title: 'Error found',
                body: error.message || 'Unable to get data.'
              }));
            }
          return;
        }
    }

    const loadAssessmentTrendData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_assessment_trend', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
          location_id, semester_id, start_date, end_date }});
          if(request.status === 200) {setAssessmentTrend(request.data.data);}
      }
      catch(error) {
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }
    
    const loadSessionTrendData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_session_trend', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setSessionTrend(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadAbsentPresentData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_absent_present', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setAbsentPresent(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadAssessmentCompletion = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_assessment_completion', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setAssessmentCompletion(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadCycleGrowthData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_cycle_growth', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setCycleGrowth(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadCycleGrowthDataPrePostMid = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_cycle_growth_delimiters', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setCycleDelimGrowth(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadLowPerformanceTutorsData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_tutor_low_performance', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setLowPerformanceTutors(request.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadAssessmentBChart = async (location_id, semester_id, start_date, end_date) => {
        try {
            const request = await API.get('/get_assessment_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
              location_id, semester_id, start_date, end_date }});
            if (request.status === 200) {setAssessmentBChart(request.data.data);}
        }
        catch(error) {
          console.log(error)
          if (error.isAuthError || error.isForbidden) {
              return;
            } 
            if (error.isForbidden) {
              return;
            } 
            if (error.isNetworkError) {
              dispatch(setSnackError({ 
                title: 'Connection Error', 
                body: error.message 
              }));
            } else {
              dispatch(setSnackError({
                title: 'Error found',
                body: error.message || 'Unable to get data.'
              }));
            }
          return;
        }
    }

    const loadProgramsBChart = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_programs_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setProgramsBChart(request.data.data);}
      }
      catch(error) {
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get program bchart.'
            }));
          }
        return;
      }
    }

    const loadTutorsBChart = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_tutors_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setTutorsBChart(request.data.data);}
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadSentimentData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_sentiment', 
              {params: { organization_id: role.organization_id, 
            location_id }});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                  const d = parseSentimentData(arr)
                  setSentimentData(d)
                  console.log(d)
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadSessionVScores = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_session_vscore', 
              {params: { organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date}});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                setSessionVScore(arr);
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadStudentVAssessments = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_student_vassessment', 
              {params: { organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date}});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                setStudentVScore(arr);
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }


    const loadTutorRecentSessions = async (location_id, semester_id) => {
      if (!location_id && !semester_id) { return ;}
      try {
          const request = await API.get('/get_recent_location_sessions', 
              {params: {location_id, semester_id }});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                  setTutorsSessions(arr);
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadLocationSessionAverage = async (location_id, semester_id) => {
    if (!location_id && !semester_id) { return ;}
    try {
        const request = await API.get('/get_location_session_average', 
            {params: { location_id, semester_id }});
        if (request.status === 200) {
            const arr = request.data.data;
            setLocationSessions(arr);
        }
    }
    catch(error) {
      console.log(error)
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        if (error.isForbidden) {
          return;
        }
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to get data.'
          }));
        }
      return;
    }
    }
    useEffect(() => {
      loadAssessmentTrendData(null, null, null, null);
      loadSessionTrendData(null, null, null, null);
      loadGuageData(null, null, null, null);
    }, []) 

  return (
    <Box sx={{ flexGrow: 1, p: 0 }}>
      <DashboardNav />
      <AdminFilterBar 
        onSubmit={handleSubmit} 
        initialFilters={initialValues} />
        {loading ? <LinearProgress color="inherit" /> : null}

      <Stack spacing={8}>
        <Box id="pulse-section">
          <Gate allow={sysRole === ROOT || canLocal} fallback={fallback("view:analytics:local")}>
              <ExecutivePulse 
              absenteeism={absentPresent} 
              sessions={sessionsBChart} 
              assessments={assessmentBChart} 
              guageData={guageData} loading={semestersLoading} />
          </Gate>
        </Box>
        <Box id="ops-section">
          <Gate allow={sysRole === ROOT || canLocal} fallback={fallback("view:analytics:local")}>
              <OperationalTrends 
                absenteeism={absentPresent} 
                programs={programsBChart} 
                tutors={tutorsBChart} 
                sessionscore={sessionVScore}
                workload={locationAverageSessions} 
                lowPerformance={lowPerformanceTutors}
                loading={semestersLoading} />
          </Gate>
        </Box>

        <Box id="impact-section">
          <Gate allow={sysRole === ROOT || canLocal} fallback={fallback("view:analytics:local")}>
              <ImpactAnalytics
              studentVScore={studentVScore}
              assessmentCompletion={assessmentCompletion}
              cycleGrowthDelim={cycleGrowthDelim}
              cycleGrowth={cycleGrowth}
              />
          </Gate>
        </Box>
        <Box id="logs-section">
          <Gate allow={sysRole === ROOT || canLocal} fallback={fallback("view:analytics:local")}>
              <LogAnalytics tutorsSessions={tutorsSessions} />
          </Gate>
        </Box>
      </Stack>
          
    </Box>
  );
};

export default AdminHome;